(() => {
  const root = document.querySelector('[data-kartolog-catalog]');
  if (!root) return;

  let items = [];
  let logoIndex = { sets: [], series: [], productCollections: {} };
  let logoLookup = {
    setsByHandle: new Map(),
    setsByTitle: new Map(),
    seriesByHandle: new Map(),
    seriesByTitle: new Map(),
  };
  const pageSize = 30;
  const feedBase = root.dataset.kartologFeed || '/collections/all?view=kartolog-data-v5';
  const cacheKey = `kartolog:catalog:v13:${window.location.pathname}:${feedBase}`;
  const cacheTtlMs = 60 * 1000;
  const feedFreshToken = String(Date.now());
  const fetchConcurrency = 6;
  const state = {
    q: '',
    page: 1,
    sort: 'newest',
    available: true,
    filters: {
      kind: '',
      series: '',
      setName: '',
      group: '',
      rarity: '',
      finish: '',
      language: '',
      location: '',
      artist: '',
    },
  };
  const filterKeys = ['rarity', 'finish', 'location'];
  const emptyLabels = {
    kind: 'Tümü',
    series: 'Tüm seriler',
    setName: 'Tüm setler',
    group: 'Tümü',
    rarity: 'Tümü',
    finish: 'Tümü',
    language: 'Tümü',
    location: 'Tüm lokasyonlar',
  };

  const el = {
    total: root.querySelector('[data-kartolog-total]'),
    count: root.querySelector('[data-kartolog-count]'),
    caption: root.querySelector('[data-kartolog-caption]'),
    searchForm: root.querySelector('[data-kartolog-search-form]'),
    search: root.querySelector('[data-kartolog-search]'),
    artist: root.querySelector('[data-kartolog-artist]'),
    available: root.querySelector('[data-kartolog-available]'),
    clear: root.querySelector('[data-kartolog-clear]'),
    filters: root.querySelector('[data-kartolog-filters]'),
    filterToggle: root.querySelector('[data-kartolog-filter-toggle]'),
    sort: root.querySelector('[data-kartolog-sort]'),
    status: root.querySelector('[data-kartolog-status]'),
    grid: root.querySelector('[data-kartolog-grid]'),
    pager: root.querySelector('[data-kartolog-pager]'),
    prev: root.querySelector('[data-kartolog-prev]'),
    next: root.querySelector('[data-kartolog-next]'),
    page: root.querySelector('[data-kartolog-page]'),
    seriesRail: root.querySelector('[data-kartolog-series-rail]'),
    setRail: root.querySelector('[data-kartolog-set-rail]'),
    seriesSummary: root.querySelector('[data-kartolog-series-summary]'),
    setSummary: root.querySelector('[data-kartolog-set-summary]'),
    languageRail: root.querySelector('[data-kartolog-language-rail]'),
    languageSummary: root.querySelector('[data-kartolog-language-summary]'),
  };

  // Let customers omit separators such as the # before a card number.
  const normalize = (value) =>
    String(value || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const escapeHtml = (value) =>
    String(value || '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[character]);

  function applyInitialQuery() {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q') || params.get('search') || '';
    if (!q) return;
    state.q = q;
    el.search.value = q;
  }

  const money = new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 2,
  });

  function feedUrl(page) {
    const url = new URL(feedBase, window.location.origin);
    url.searchParams.set('page', String(page));
    url.searchParams.set('_kt', feedFreshToken);
    return url;
  }

  function readCache() {
    try {
      const cached = JSON.parse(window.localStorage.getItem(cacheKey) || 'null');
      if (!cached || !Array.isArray(cached.items)) return null;
      if (Date.now() - Number(cached.savedAt || 0) > cacheTtlMs) return null;
      return cached.items;
    } catch {
      return null;
    }
  }

  function writeCache(nextItems) {
    try {
      window.localStorage.setItem(cacheKey, JSON.stringify({
        savedAt: Date.now(),
        items: nextItems,
      }));
    } catch {
      // Catalog data can exceed browser storage on some devices; the page still works without cache.
    }
  }

  function isPokemonItem(item) {
    return normalize(item.kind) === 'pokemon';
  }

  function catalogItems(nextItems) {
    return (nextItems || []).filter(isPokemonItem);
  }

  async function loadLogoIndex() {
    const url = root.dataset.kartologLogoIndex;
    if (!url) return;
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return;
      const payload = await response.json();
      logoIndex = {
        sets: Array.isArray(payload.sets) ? payload.sets : [],
        series: Array.isArray(payload.series) ? payload.series : [],
        productCollections: payload.productCollections && typeof payload.productCollections === 'object' ? payload.productCollections : {},
      };
      rebuildLogoLookup();
      if (items.length) render();
    } catch {
      logoIndex = { sets: [], series: [], productCollections: {} };
      rebuildLogoLookup();
    }
  }

  function rebuildLogoLookup() {
    const next = {
      setsByHandle: new Map(),
      setsByTitle: new Map(),
      seriesByHandle: new Map(),
      seriesByTitle: new Map(),
    };
    for (const entry of logoIndex.sets || []) {
      if (entry.collectionHandle) next.setsByHandle.set(normalize(entry.collectionHandle), entry);
      if (entry.collectionTitle) next.setsByTitle.set(normalize(entry.collectionTitle), entry);
      if (entry.name) next.setsByTitle.set(normalize(entry.name), entry);
    }
    for (const entry of logoIndex.series || []) {
      if (entry.collectionHandle) next.seriesByHandle.set(normalize(entry.collectionHandle), entry);
      if (entry.collectionTitle) next.seriesByTitle.set(normalize(entry.collectionTitle), entry);
      if (entry.name) next.seriesByTitle.set(normalize(entry.name), entry);
    }
    logoLookup = next;
  }

  async function fetchFeedPage(page) {
    for (let attempt = 1; attempt <= 5; attempt += 1) {
      const response = await fetch(feedUrl(page), { credentials: 'same-origin' });
      const body = await response.text();
      if (response.ok) {
        try {
          return JSON.parse(body);
        } catch {
          // Shopify storefront may briefly return an HTML edge response while the view warms up.
        }
      }
      if (attempt < 5) {
        await new Promise((resolve) => window.setTimeout(resolve, attempt * 900));
        continue;
      }
      throw new Error(`Shopify ürünleri okunamadı (${response.status}).`);
    }
  }

  async function loadItems(onFirstPage) {
    const first = await fetchFeedPage(1);
    const pages = Math.max(1, Number(first.pages) || 1);
    const loaded = [...(first.items || [])];
    if (typeof onFirstPage === 'function') onFirstPage(loaded, pages);
    let nextPage = 2;

    async function worker() {
      while (nextPage <= pages) {
        const page = nextPage++;
        const payload = await fetchFeedPage(page);
        loaded.push(...(payload.items || []));
      }
    }

    await Promise.all(Array.from({ length: Math.min(fetchConcurrency, pages - 1) }, worker));
    return loaded;
  }

  function optionValues(key, sourceItems, selected, sortByReleaseDate = false) {
    const counts = new Map();
    for (const item of sourceItems) {
      for (const value of filterValues(item, key)) {
        if (!value) continue;
        counts.set(value, (counts.get(value) || 0) + 1);
      }
    }
    if (selected && !counts.has(selected)) counts.set(selected, 0);
    return [...counts.entries()].sort((a, b) => {
      if (sortByReleaseDate) {
        const dateDiff = taxonomyReleaseDate(key, b[0]) - taxonomyReleaseDate(key, a[0]);
        if (dateDiff) return dateDiff;
      }
      return a[0].localeCompare(b[0], 'tr');
    });
  }

  function itemNewestDate(item) {
    return Math.max(
      Date.parse(item.createdAt || '') || 0,
      Date.parse(item.publishedAt || '') || 0,
      Date.parse(item.updatedAt || '') || 0,
    );
  }

  function taxonomyReleaseDate(key, value) {
    if (!value) return Number.MAX_SAFE_INTEGER;
    const source = key === 'series' ? logoIndex.series : logoIndex.sets;
    const match = findLogoEntry(source, value);
    return Date.parse(match?.releaseDate || '') || 0;
  }

  function findLogoEntry(entries, value, extraPredicate = null) {
    const query = normalize(value);
    if (!query) return null;
    const aliases = new Set([
      query,
      query.replace(/\bset\b/g, '').replace(/\s+/g, ' ').trim(),
      `${query} set`.replace(/\s+/g, ' ').trim(),
    ]);
    return entries.find((entry) => aliases.has(normalize(entry.name)) && (!extraPredicate || extraPredicate(entry)))
      || entries.find((entry) => {
        const name = normalize(entry.name);
        if (extraPredicate && !extraPredicate(entry)) return false;
        return [...aliases].some((alias) => alias && (name === alias || name.includes(alias) || alias.includes(name)));
      })
      || null;
  }

  function matchesExcept(item, excludedFilterKeys = []) {
    const excluded = new Set(excludedFilterKeys);
    if (state.available && !item.available) return false;
    for (const [key, selected] of Object.entries(state.filters)) {
      if (!selected || excluded.has(key)) continue;
      if (key === 'artist') {
        if (!normalize(item.artist).includes(normalize(selected))) return false;
      } else if (!filterValues(item, key).includes(selected)) {
        return false;
      }
    }
    if (!state.q) return true;
    return normalize(
      [
        item.title,
        item.number,
        ...filterValues(item, 'series'),
        ...filterValues(item, 'setName'),
        item.artist,
        item.rarity,
        item.finish,
        item.group,
        item.language,
        ...(item.tags || []),
      ].join(' '),
    ).includes(normalize(state.q));
  }

  function filterValues(item, key) {
    if (key === 'location') {
      return [
        item.istanbulAvailable ? 'İstanbul' : '',
        item.ankaraAvailable ? 'Ankara' : '',
      ].filter(Boolean);
    }
    if (key === 'setName') {
      const collectionSets = collectionTaxonomyValues(item, 'setName');
      if (collectionSets.length) return collectionSets;
    }
    if (key === 'series') {
      const collectionSeries = collectionTaxonomyValues(item, 'series');
      if (collectionSeries.length) return collectionSeries;
    }
    return [String(item[key] || '').trim()];
  }

  function collectionTaxonomyValues(item, key) {
    const mappedCollections = logoIndex.productCollections?.[item.handle];
    const collections = Array.isArray(item.collections) && item.collections.length
      ? item.collections
      : Array.isArray(mappedCollections)
        ? mappedCollections
        : [];
    if (!collections.length) return [];
    const values = [];
    const push = (value) => {
      const clean = String(value || '').trim();
      if (clean && !values.includes(clean)) values.push(clean);
    };
    for (const collection of collections) {
      const handle = normalize(collection.handle);
      const title = normalize(collection.title);
      const setEntry = logoLookup.setsByHandle.get(handle) || logoLookup.setsByTitle.get(title);
      const seriesEntry = logoLookup.seriesByHandle.get(handle) || logoLookup.seriesByTitle.get(title);
      if (key === 'setName') {
        if (setEntry) push(setEntry.name);
      } else if (key === 'series') {
        if (seriesEntry) push(seriesEntry.name);
        if (setEntry?.series) push(setEntry.series);
      }
    }
    return values;
  }

  function bindSelect(key) {
    const select = root.querySelector(`[data-kartolog-filter="${key}"]`);
    if (!select) return;
    select.addEventListener('change', () => {
      state.filters[key] = select.value;
      if (key === 'series') clearInvalidSetForSeries();
      state.page = 1;
      render();
    });
  }

  function selectSeries(value) {
    state.filters.series = value || '';
    clearInvalidSetForSeries();
    state.page = 1;
    render();
  }

  function selectSet(value) {
    state.filters.setName = value || '';
    state.page = 1;
    render();
  }

  function selectLanguage(value) {
    state.filters.language = value || '';
    state.filters.series = '';
    state.filters.setName = '';
    state.page = 1;
    render();
  }

  function renderLanguageSwitch() {
    if (!el.languageRail) return;
    el.languageRail.querySelectorAll('[data-kartolog-language-value]').forEach((button) => {
      const active = (button.dataset.kartologLanguageValue || '') === state.filters.language;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    if (el.languageSummary) {
      const label = state.filters.language === 'English'
        ? 'İngilizce kartlar'
        : state.filters.language === 'Japanese'
          ? 'Japonca kartlar'
          : 'Tüm diller';
      el.languageSummary.textContent = label;
    }
  }

  function clearInvalidSetForSeries() {
    if (!state.filters.setName || !state.filters.series) return;
    const validSets = new Set(
      items
        .filter((item) => matchesExcept(item, ['setName']) && filterValues(item, 'series').includes(state.filters.series))
        .flatMap((item) => filterValues(item, 'setName')),
    );
    if (!validSets.has(state.filters.setName)) state.filters.setName = '';
  }

  function looseLogoMatch(entries, value, extraPredicate = null) {
    return findLogoEntry(entries, value, extraPredicate)?.logo || '';
  }

  function taxonomyImage(sourceItems, key, value) {
    if (!value) return '';
    if (key === 'setName') {
      const selectedSeries = state.filters.series;
      const logo = looseLogoMatch(
        logoIndex.sets,
        value,
        selectedSeries ? (entry) => normalize(entry.series) === normalize(selectedSeries) : null,
      ) || looseLogoMatch(logoIndex.sets, value);
      if (logo) return logo;
    }
    if (key === 'series') {
      const logo = looseLogoMatch(logoIndex.series, value) || looseLogoMatch(logoIndex.sets, value);
      if (logo) return logo;
    }
    const match = sourceItems.find((item) => filterValues(item, key).includes(value) && item.imageUrl);
    return match?.imageUrl || '';
  }

  function taxonomyButton(type, value, count, active, imageUrl = '') {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `kartolog-taxonomy__chip${active ? ' is-active' : ''}`;
    button.dataset[type === 'series' ? 'kartologSeriesValue' : 'kartologSetValue'] = value;
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    const label = value || (type === 'series' ? 'Tüm seriler' : 'Tüm setler');
    const image = imageUrl
      ? `<span class="kartolog-taxonomy__visual"><img src="${escapeHtml(imageUrl)}" alt="" loading="lazy"></span>`
      : '<span class="kartolog-taxonomy__visual is-empty" aria-hidden="true"></span>';
    button.innerHTML = `${image}<span class="kartolog-taxonomy__text"><strong>${escapeHtml(label)}</strong><small>${Number(count || 0).toLocaleString('tr-TR')} ürün</small></span>`;
    return button;
  }

  function renderTaxonomy() {
    if (!el.seriesRail || !el.setRail) return;

    const selectedSeries = state.filters.series;
    const selectedSet = state.filters.setName;
    const seriesSource = items.filter((item) => matchesExcept(item, ['series', 'setName']));
    const seriesOptions = optionValues('series', seriesSource, selectedSeries, true);
    const setSource = items.filter((item) => (
      matchesExcept(item, ['setName']) && (!selectedSeries || filterValues(item, 'series').includes(selectedSeries))
    ));
    const setOptions = optionValues('setName', setSource, selectedSet, true);

    el.seriesRail.replaceChildren(
      taxonomyButton('series', '', seriesSource.length, !selectedSeries),
      ...seriesOptions.map(([value, count]) => taxonomyButton('series', value, count, value === selectedSeries, taxonomyImage(seriesSource, 'series', value))),
    );
    el.setRail.replaceChildren(
      taxonomyButton('set', '', setSource.length, !selectedSet),
      ...setOptions.map(([value, count]) => taxonomyButton('set', value, count, value === selectedSet, taxonomyImage(setSource, 'setName', value))),
    );

    if (el.seriesSummary) {
      el.seriesSummary.textContent = selectedSeries ? `${selectedSeries} seçili` : `${seriesOptions.length.toLocaleString('tr-TR')} seri`;
    }
    if (el.setSummary) {
      el.setSummary.textContent = selectedSet
        ? `${selectedSet} seçili`
        : selectedSeries
          ? `${setOptions.length.toLocaleString('tr-TR')} set`
          : 'Seri seçersen setler daralır';
    }
  }

  function refreshFacetSelects() {
    for (const key of filterKeys) {
      const select = root.querySelector(`[data-kartolog-filter="${key}"]`);
      if (!select) continue;
      const selected = state.filters[key];
      const empty = document.createElement('option');
      empty.value = '';
      empty.textContent = emptyLabels[key];
      const options = optionValues(key, items.filter((item) => matches(item, key)), selected).map(([value, count]) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = `${value} (${count})`;
        return option;
      });
      select.replaceChildren(empty, ...options);
      select.value = selected;
    }
  }

  function matches(item, excludedFilterKey = '') {
    if (state.available && !item.available) return false;
    for (const [key, selected] of Object.entries(state.filters)) {
      if (!selected || key === excludedFilterKey) continue;
      if (key === 'artist') {
        if (!normalize(item.artist).includes(normalize(selected))) return false;
      } else if (key === 'location') {
        if (!filterValues(item, key).includes(selected)) return false;
      } else if (String(item[key] || '') !== selected) {
        return false;
      }
    }
    if (!state.q) return true;
    return normalize(
      [
        item.title,
        item.number,
        item.series,
        item.setName,
        item.artist,
        item.rarity,
        item.finish,
        item.group,
        item.language,
        ...(item.tags || []),
      ].join(' '),
    ).includes(normalize(state.q));
  }

  function sorted(filtered) {
    const next = [...filtered];
    if (state.sort === 'price-asc') next.sort((a, b) => Number(a.price) - Number(b.price));
    if (state.sort === 'price-desc') next.sort((a, b) => Number(b.price) - Number(a.price));
    if (state.sort === 'title') next.sort((a, b) => a.title.localeCompare(b.title, 'tr'));
    if (state.sort === 'newest') {
      next.sort((a, b) => {
        const bDate = itemNewestDate(b);
        const aDate = itemNewestDate(a);
        return bDate - aDate;
      });
    }
    return next;
  }

  function clampQuantity(value, maximum) {
    return Math.min(Math.max(1, Number.parseInt(value, 10) || 1), maximum);
  }

  async function updateCartBubble() {
    const routesRoot = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${routesRoot}cart.js`, { credentials: 'same-origin' });
    if (!response.ok) return;
    const cart = await response.json();
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return;
    const visibleCount = bubble.querySelector('span[aria-hidden="true"]') || bubble.querySelector('span');
    const hiddenCount = bubble.querySelector('.visually-hidden');
    if (visibleCount) visibleCount.textContent = String(cart.item_count || 0);
    if (hiddenCount) hiddenCount.textContent = `${cart.item_count || 0} ürün`;
  }

  async function addToCart(item, quantity, button, status) {
    const routesRoot = window.Shopify?.routes?.root || '/';
    button.disabled = true;
    status.textContent = 'Ekleniyor...';
    status.className = 'kartolog-product-card__cart-status';

    try {
      const response = await fetch(`${routesRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: Number(item.variantId), quantity }],
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || 'Ürün sepete eklenemedi.');
      status.textContent = 'Sepete eklendi';
      status.classList.add('is-success');
      await updateCartBubble();
    } catch (error) {
      status.textContent = error.message || 'Ürün sepete eklenemedi.';
      status.classList.add('is-error');
    } finally {
      button.disabled = !item.available || !item.variantId;
    }
  }

  function productCard(item) {
    const card = document.createElement('article');
    card.className = 'kartolog-product-card';
    const href = `/products/${encodeURIComponent(item.handle)}`;
    const meta = [item.setName, item.number ? `#${item.number}` : '', item.artist ? `Çizer: ${item.artist}` : ''].filter(Boolean).join(' · ');
    const locations = [
      item.istanbulAvailable ? 'İstanbul' : '',
      item.ankaraAvailable ? 'Ankara' : '',
    ].filter(Boolean);
    const locationText = locations.length ? locations.join(', ') : 'Bilgi yok';
    const maximum = Math.max(1, Number(item.maxQuantity) || 1);
    const unavailable = !item.available || !item.variantId;
    const imageSrcSet = item.imageSrcSet
      ? ` srcset="${escapeHtml(item.imageSrcSet)}" sizes="(min-width: 1180px) 320px, (min-width: 990px) 28vw, (min-width: 640px) 46vw, 92vw"`
      : '';

    card.innerHTML = `
      <a class="kartolog-product-card__image" href="${href}">
        ${(item.istanbulAvailable || item.ankaraAvailable) ? `<span class="kartolog-product-card__badges">${item.istanbulAvailable ? '<span>IST</span>' : ''}${item.ankaraAvailable ? '<span>ANK</span>' : ''}</span>` : ''}
        <img src="${escapeHtml(item.imageUrl)}"${imageSrcSet} alt="${escapeHtml(item.title)}" loading="lazy" decoding="async" width="${Number(item.imageWidth) || 720}" height="${Number(item.imageHeight) || 1004}">
      </a>
      <div class="kartolog-product-card__body">
        <a class="kartolog-product-card__title" href="${href}">${escapeHtml(item.title)}</a>
        ${item.artist ? `<p class="kartolog-product-card__meta">${escapeHtml(`Çizer: ${item.artist}`)}</p>` : ''}
        <div class="kartolog-product-card__foot">
          <strong class="kartolog-product-card__price">${money.format(Number(item.price) || 0)}</strong>
          <span class="kartolog-product-card__pc">${escapeHtml(normalize(item.language).includes('japanese') ? 'PC x 1.44' : 'PC x 1.8')}</span>
        </div>
        <div class="kartolog-product-card__cart">
          <div class="kartolog-product-card__quantity">
            <button type="button" aria-label="Adedi azalt" data-kartolog-quantity-dec>-</button>
            <input type="number" inputmode="numeric" min="1" max="${maximum}" value="1" aria-label="Adet" data-kartolog-quantity>
            <button type="button" aria-label="Adedi artır" data-kartolog-quantity-inc>+</button>
          </div>
          <button type="button" class="kartolog-product-card__add" data-kartolog-add ${unavailable ? 'disabled' : ''}>${unavailable ? 'Tükendi' : 'Sepete ekle'}</button>
        </div>
        <p class="kartolog-product-card__cart-status" aria-live="polite"></p>
      </div>
    `;

    const quantityInput = card.querySelector('[data-kartolog-quantity]');
    const decrement = card.querySelector('[data-kartolog-quantity-dec]');
    const increment = card.querySelector('[data-kartolog-quantity-inc]');
    const add = card.querySelector('[data-kartolog-add]');
    const status = card.querySelector('.kartolog-product-card__cart-status');

    decrement.addEventListener('click', () => {
      quantityInput.value = String(clampQuantity(Number(quantityInput.value) - 1, maximum));
    });
    increment.addEventListener('click', () => {
      quantityInput.value = String(clampQuantity(Number(quantityInput.value) + 1, maximum));
    });
    quantityInput.addEventListener('change', () => {
      quantityInput.value = String(clampQuantity(quantityInput.value, maximum));
    });
    add.addEventListener('click', () => addToCart(item, clampQuantity(quantityInput.value, maximum), add, status));

    return card;
  }

  function render() {
    renderLanguageSwitch();
    refreshFacetSelects();
    renderTaxonomy();
    const filtered = sorted(items.filter(matches));
    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    state.page = Math.min(state.page, pages);
    const start = (state.page - 1) * pageSize;
    const pageItems = filtered.slice(start, start + pageSize);

    if (el.total) el.total.textContent = items.length.toLocaleString('tr-TR');
    el.count.textContent = `${filtered.length.toLocaleString('tr-TR')} ürün bulundu`;
    el.caption.textContent = `${items.length.toLocaleString('tr-TR')} Shopify ürünü arasından gösteriliyor`;
    el.grid.replaceChildren(...pageItems.map(productCard));
    el.status.hidden = pageItems.length > 0;
    el.status.textContent = pageItems.length ? '' : 'Bu filtrelerle eşleşen ürün bulunamadı.';
    el.pager.hidden = pages <= 1;
    el.page.textContent = `${state.page} / ${pages}`;
    el.prev.disabled = state.page <= 1;
    el.next.disabled = state.page >= pages;
  }

  filterKeys.forEach(bindSelect);

  el.searchForm.addEventListener('submit', (event) => {
    event.preventDefault();
    state.q = el.search.value;
    state.page = 1;
    render();
  });

  el.artist.addEventListener('input', () => {
    state.filters.artist = el.artist.value;
    state.page = 1;
    render();
  });

  el.available.addEventListener('change', () => {
    state.available = el.available.checked;
    state.page = 1;
    render();
  });

  el.sort.addEventListener('change', () => {
    state.sort = el.sort.value;
    state.page = 1;
    render();
  });

  el.clear.addEventListener('click', () => {
    state.q = '';
    state.page = 1;
    state.available = true;
    state.filters.language = '';
    state.filters.series = '';
    state.filters.setName = '';
    el.search.value = '';
    el.artist.value = '';
    el.available.checked = true;
    root.querySelectorAll('[data-kartolog-filter]').forEach((select) => {
      select.value = '';
      state.filters[select.dataset.kartologFilter] = '';
    });
    state.filters.artist = '';
    render();
  });

  if (el.seriesRail) {
    el.seriesRail.addEventListener('click', (event) => {
      const button = event.target.closest('[data-kartolog-series-value]');
      if (!button) return;
      selectSeries(button.dataset.kartologSeriesValue || '');
    });
  }

  if (el.setRail) {
    el.setRail.addEventListener('click', (event) => {
      const button = event.target.closest('[data-kartolog-set-value]');
      if (!button) return;
      selectSet(button.dataset.kartologSetValue || '');
    });
  }

  if (el.languageRail) {
    el.languageRail.addEventListener('click', (event) => {
      const button = event.target.closest('[data-kartolog-language-value]');
      if (!button) return;
      selectLanguage(button.dataset.kartologLanguageValue || '');
    });
  }

  root.querySelectorAll('[data-kartolog-taxonomy-scroll]').forEach((button) => {
    button.addEventListener('click', () => {
      const rail = button.dataset.kartologTaxonomyScroll === 'series' ? el.seriesRail : el.setRail;
      if (!rail) return;
      const direction = Number(button.dataset.direction) || 1;
      rail.scrollBy({
        left: direction * Math.max(280, Math.round(rail.clientWidth * 0.82)),
        behavior: 'smooth',
      });
    });
  });

  el.filterToggle.addEventListener('click', () => el.filters.classList.toggle('is-visible'));
  el.prev.addEventListener('click', () => {
    state.page -= 1;
    render();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  el.next.addEventListener('click', () => {
    state.page += 1;
    render();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  applyInitialQuery();
  loadLogoIndex();
  const cachedItems = readCache();
  if (cachedItems && cachedItems.length) {
    items = catalogItems(cachedItems);
    render();
    el.status.hidden = false;
    el.status.textContent = 'Katalog hafızadan gösteriliyor, güncel stok arkada kontrol ediliyor...';
  } else {
    el.status.hidden = false;
    el.status.textContent = 'Kartolog kataloğu hazırlanıyor...';
  }

  loadItems((firstPageItems, pages) => {
    if (cachedItems && cachedItems.length) return;
    items = catalogItems(firstPageItems);
    render();
    if (pages > 1) {
      el.status.hidden = false;
      el.status.textContent = 'İlk kartlar gösterildi, kalan katalog tamamlanıyor...';
    }
  })
    .then((loadedItems) => {
      items = catalogItems(loadedItems);
      writeCache(loadedItems);
      render();
    })
    .catch((error) => {
      el.status.hidden = false;
      el.status.textContent = error.message;
    });
})();
